import React, { useCallback, useEffect, useState } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  AppBar,
  Button,
  Grid,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import WeekDaysSelection from './WeekDaysSelection';
import ZoneListItem from './ZoneListItem';
import Checkbox from '@material-ui/core/Checkbox';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  clearAll,
  getBaseOperationHours,
  getBaseOperationHoursPermission,
  setBaseOperationHours,
  setOperationHour,
} from '../../redux/actions/operationHoursActions';
import {
  toggleOperationHoursViewMode,
  setRecentlyEditedBaseHours,
} from '../../redux/actions/newOperationHoursActions';
import type {
  RecentlyEditedBaseHours,
  RecentlyEditedDayData,
} from '../OperationHoursViewTab/hooks/useServiceData';
import { setEdited } from '../../redux/actions/routineDaysActions';
import { useParams } from 'react-router-dom';
import { selectHoursConverted } from '../../redux/selectors/regionsSelector';
import { arrivalTimes, times as dropdownTimes } from '../../utils/utils';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { useTranslation } from 'react-i18next';
import {
  calculateDeliveryTimeBase,
  toMinutes,
  validateHourRange,
  canSeeDeliveryRow,
  isCorporateAdmin,
} from '../../utils/helpers';
import moment from 'moment';
import {
  operationHoursPermission,
  otherBasePermission,
} from './constants/OperationHoursPermission';
import RBISuccessPopup from '../RBIMessagePopup/RBISuccessPopup';
import ShowErrorMessageModal from './RestaurantDetails/ShowErrorMessageModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import RBIFollowCursorTooltip from '../RBIFollowCursorTooltip/RBIFollowCursorTooltip';
import { useGeneralTooltip } from '../../utils/tooltipUtils';
import { getAutoCalculatedTHDaypartHours } from '../../utils/calculateDaypartHours';
import { ServiceTime } from '../../interfaces/daypart';

const varToString = varObj => Object.keys(varObj)[0];
const hourNames = [
  'drIsOpened24Hours',
  'dtIsOpened24Hours',
  'digitalDtIsOpened24Hours',
  'dhIsOpened24Hours',
];
const closeNames = [
  'drIsClosed',
  'dtIsClosed',
  'digitalDtIsClosed',
  'dhIsClosed',
  'cateringIsClosed',
  'curbsideIsClosed',
];
const isPLKAutoCalculatedLateNight = (brand, Region, countries) =>
  brand === 'PLK' &&
  (Region === 'NA' ||
    (countries.length > 0 &&
      countries?.every(val => val.name?.split(' - ')[0] === 'NA')));

function BaseAdjustment({ classes }: any) {
  const matches = useMediaQuery('(min-width:1280px)');
  const [checkedAllTF, setCheckedAllTF] = useState<boolean>(false);
  const weekDays = useSelector(
    (state: RootStateOrAny) => state.OperationHoursReducer.weekDays,
  );
  interface ParamTypes {
    id: string;
  }
  const { permissions, userInfo } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const { id } = useParams<ParamTypes>();
  const [checkedAllClose, setCheckedAllClose] = useState<boolean>(false);
  const [enableBtnEdited, setEnableBtnEdited] = useState<boolean>(true);
  const [successPopupModal, setSuccessPopupModal] = useState({
    isAlert: false,
    isOpen: false,
    message: '',
    shouldRedirectOnClose: false,
  });
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [uploadErrors, setErrors] = useState();
  const dispatch: any = useDispatch();

  const isMDSUpAndRunning = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer.isMDSUpAndRunning,
  );
  const { ramWarningBarToggle } = useFlags();
  //Pulling times from redux
  const {
    deliveryTimeOpen,
    deliveryTimeClose,
    drTimeOpen,
    drTimeClose,
    drIsOpened24Hours = 'N',
    drIsClosed = 'N',
    dtTimeOpen,
    dtTimeClose,
    digitalDtTimeOpen,
    digitalDtTimeClose,
    dtIsOpened24Hours = 'N',
    dtIsClosed,
    digitalDtIsOpened24Hours = 'N',
    digitalDtIsClosed,
    dhIsOpened24Hours = 'N',
    cateringTimeOpen,
    cateringTimeClose,
    curbsideTimeOpen,
    curbsideTimeClose,
    breakfastTimeOpen,
    breakfastTimeClose,
    lunchTimeOpen,
    lunchTimeClose,
    snackTimeOpen,
    snackTimeClose,
    dinnerTimeOpen,
    dinnerTimeClose,
    lateNightTimeOpen,
    lateNightTimeClose,
    deliveryTimeOpenMode,
    deliveryTimeCloseMode,
    drTimeOpenMode,
    drTimeCloseMode,
    dtTimeOpenMode,
    dtTimeCloseMode,
    digitalDtTimeOpenMode,
    digitalDtTimeCloseMode,
    cateringTimeOpenMode,
    cateringTimeCloseMode,
    curbsideTimeOpenMode,
    curbsideTimeCloseMode,
    breakfastTimeOpenMode,
    breakfastTimeCloseMode,
    lunchTimeOpenMode,
    lunchTimeCloseMode,
    snackTimeOpenMode,
    snackTimeCloseMode,
    dinnerTimeOpenMode,
    dinnerTimeCloseMode,
    lateNightTimeOpenMode,
    lateNightTimeCloseMode,
    isCalculated = 'N',
    arrivalTime,
    departureTime,
    cateringIsClosed,
    curbsideIsClosed,
    dhIsClosed,
  } = useSelector(
    (state: RootStateOrAny) => state.OperationHoursReducer.entity,
  );

  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );

  const { hasEditHoursOfOperation, hasViewMenuItemOperationHours } =
    useSelector((state: RootStateOrAny) => state.UserReducer.thisUser);

  const { Region, Country, CountryId, RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  const { t } = useTranslation();
  const isRestaurantClosed = ['Closed', 'Temp Closed'].includes(
    RestaurantStatus,
  );

  const { selectedRestaurantsList: selectedRestaurants } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer,
  );

  const countries = useSelector(
    (state: RootStateOrAny) =>
      state.ReportingUnitsReducer?.reportingUnitsCountries,
  );
  const curbsideService = restaurantServices.find(
    elem => elem.code === 'curbsidePickup',
  );
  const cateringService = restaurantServices.find(
    elem => elem.code === 'catering',
  );
  const driveThruService = restaurantServices.find(
    elem => elem.code === 'driveThru',
  );
  const digitalDriveThruService = restaurantServices.find(
    elem => elem.code === 'digitalDriveThru',
  );
  const { deliveryHours } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );

  const { entity, loading, loaded } = useSelector(
    (state: RootStateOrAny) => state.OperationHoursReducer,
  );

  const [checkedCB, setCheckedCB] = useState<string[]>(['N', 'N', 'N', 'N']);

  const [checkedClose, setCheckedClose] = useState<string[]>([
    'N',
    'N',
    'N',
    'N',
    'N',
    'N',
  ]);

  // Store original base hours per day for change tracking
  const [originalBaseHoursByDay, setOriginalBaseHoursByDay] = useState<
    Record<string, Record<string, string | undefined>>
  >({});

  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );

  const hasEditPermission = hasEditAccess();
  const hasEditDaypartHoursPermission = hasEditDaypartHoursAccess(brand);

  useEffect(() => {
    setCheckedClose([
      drIsClosed,
      dtIsClosed,
      digitalDtIsClosed,
      dhIsClosed,
      cateringIsClosed,
      curbsideIsClosed,
    ]);

    let array24Hrs = [drIsOpened24Hours, dtIsOpened24Hours];
    if (brand === 'BK' && !id) {
      array24Hrs = [...array24Hrs, digitalDtIsOpened24Hours, dhIsOpened24Hours];
      setCheckedCB(array24Hrs);
      return;
    }
    const restaurantIsBKNA = brand === 'BK' && Region === 'NA';
    const restaurantIsCanada = Country === 'Canada' && CountryId === 36;
    if (restaurantIsBKNA) array24Hrs.push(digitalDtIsOpened24Hours);
    if (restaurantIsBKNA && restaurantIsCanada)
      array24Hrs.push(dhIsOpened24Hours);
    setCheckedCB(array24Hrs);
  }, [
    drIsOpened24Hours,
    dtIsOpened24Hours,
    digitalDtIsOpened24Hours,
    dhIsOpened24Hours,
    drIsClosed,
    dtIsClosed,
    digitalDtIsClosed,
    cateringIsClosed,
    curbsideIsClosed,
    dhIsClosed,
  ]);

  useEffect(() => {
    // Auto calculate daypart hours only for TH brand and Canada region
    if (brand !== 'TH' || Country !== 'Canada') return;
    // Auto calculate daypart hours for TH only if the user is franchisee user
    if (isCorporateAdmin()) return;
    const serviceTime: ServiceTime[] = [
      {
        open: drTimeOpen,
        close: drTimeClose,
        isOpened24Hours: drIsOpened24Hours,
        isClosed: drIsClosed,
      },
      {
        open: dtTimeOpen,
        close: dtTimeClose,
        isOpened24Hours: dtIsOpened24Hours,
        isClosed: dtIsClosed,
      },
    ];
    const autoCalculatedTHDaypartHours =
      getAutoCalculatedTHDaypartHours(serviceTime);
    Object.values(autoCalculatedTHDaypartHours)
      .flatMap(daypartHours => Object.entries(daypartHours))
      .forEach(([hourName, hourValue]) => {
        dispatch(setOperationHour({ hourName, hourValue }));
      });
  }, [
    drTimeOpen,
    drTimeClose,
    dtTimeOpen,
    dtTimeClose,
    drIsOpened24Hours,
    dtIsOpened24Hours,
    drIsClosed,
    dtIsClosed,
    brand,
    Country,
  ]);

  //this whole following thing is patched until the right us comes up
  const { restaurant, dayOfWeek, ...treatedPayload } = entity;
  const treatedHours: any = selectHoursConverted(treatedPayload);

  let finalPayload: any;

  useEffect(() => {
    finalPayload = {
      ...treatedHours,
      weekDays,
    };
  }, [brand, treatedHours, weekDays]);

  // Capture original base hours for the current weekday when fresh data is loaded from API
  useEffect(() => {
    if (entity && entity.dayOfWeek && !loading && loaded) {
      const currentDayKey = entity.dayOfWeek;

      setOriginalBaseHoursByDay(prev => {
        // Only store original base hours if we don't already have them for this specific day
        if (!prev[currentDayKey]) {
          return {
            ...prev,
            [currentDayKey]: { ...entity },
          };
        }
        return prev;
      });
    }
  }, [entity, loading, loaded]);

  const tooltipMessage = useGeneralTooltip({
    ramWarningBarToggle,
    isMDSUpAndRunning,
    restaurantId: id,
    isRestaurantClosed,
  });

  const handleSuccessCloseModal = () => {
    const shouldRedirect = successPopupModal.shouldRedirectOnClose;

    setSuccessPopupModal({
      isAlert: false,
      isOpen: false,
      message: '',
      shouldRedirectOnClose: false,
    });

    // Only redirect to view mode for pending process success popup
    if (shouldRedirect) {
      dispatch(toggleOperationHoursViewMode(true));
    }
  };

  const handleEditionSubmit = async (e: React.MouseEvent) => {
    //Object that will be sent to the redux action to change everything hours / if is open 24 or closed must be here
    const request = { ...finalPayload, weekDays };

    const validateRequest = function (request, name, msj) {
      const startTime = request[`${name}TimeOpen`];
      const closeTime = request[`${name}TimeClose`];
      if (startTime === null || closeTime === null) {
        if (startTime === null && closeTime === null) {
          return true;
        } else {
          dispatch(
            snackbarShowMessage(t('operationHours.pairMandatory') + ' ' + msj),
          );
          return false;
        }
      }

      if (['dr', 'dt'].includes(name)) {
        const validHourRange = validateHourRange(startTime, closeTime, 30);
        if (!validHourRange) {
          dispatch(snackbarShowMessage(t('operationHours.error.littleRange')));
          return false;
        }
      }
    };
    const daypartsValidation = !isPLKAutoCalculatedLateNight(
      brand,
      Region,
      countries,
    )
      ? [
          validateRequest(
            request,
            'breakfast',
            t('operationHours.breakfastTime'),
          ),
          validateRequest(request, 'lunch', t('operationHours.lunchTime')),
          validateRequest(request, 'snack', t('operationHours.snackTime')),
          validateRequest(request, 'dinner', t('operationHours.dinnerTime')),
          validateRequest(request, 'lateNight', t('operationHours.lateNight')),
        ]
      : [];

    const validation = [
      validateRequest(request, 'dr', t('operationHours.diningRoom')),
      ...daypartsValidation,
    ];
    if (
      curbsideService &&
      curbsideService.value &&
      checkedClose[5] === 'N' &&
      !isPLKAutoCalculatedLateNight(brand, Region, countries)
    ) {
      validation.splice(
        2,
        0,
        validateRequest(request, 'curbside', t('operationHours.curbsideTime')),
      );
    }

    if (
      cateringService &&
      cateringService.value &&
      checkedClose[4] === 'N' &&
      !isPLKAutoCalculatedLateNight(brand, Region, countries)
    ) {
      validation.splice(
        2,
        0,
        validateRequest(request, 'catering', t('operationHours.cateringTime')),
      );
    }

    if (driveThruService && driveThruService.value) {
      validation.splice(
        2,
        0,
        validateRequest(request, 'dt', t('operationHours.driveThru')),
      );
    }

    if (digitalDriveThruService && digitalDriveThruService.value) {
      validation.splice(
        2,
        0,
        validateRequest(
          request,
          'digitalDt',
          t('operationHours.mobileOrderDriveThru'),
        ),
      );
    }

    if (validation.includes(false)) {
      return;
    }

    if (!arrivalTime && !checkedAllClose) {
      dispatch(snackbarShowMessage(t('operationHours.arrivalTimeMandatory')));
      return;
    }
    if (
      !departureTime &&
      !checkedAllClose &&
      brand === 'TH' &&
      (Region === 'NA' || !id)
    ) {
      dispatch(snackbarShowMessage(t('operationHours.DepartureTimeMandatory')));
      return;
    } else if (brand === 'TH' && (Region === 'NA' || !id)) {
      request.departureTime = departureTime ? departureTime.trim() : null;
    }

    if (
      !drTimeOpen &&
      (drIsClosed === 'N' || !drIsClosed) &&
      (drIsOpened24Hours === 'N' || !drIsOpened24Hours)
    ) {
      dispatch(
        snackbarShowMessage(t('operationHours.diningRoomTimeMandatory')),
      );
      return;
    }
    if (request.drIsOpened24Hours === 'Y') {
      request.drTimeOpen = '00:00';
      request.drTimeClose = '23:59';
    }

    if (driveThruService && driveThruService.value) {
      if (
        id &&
        !dtTimeOpen &&
        (dtIsClosed === 'N' || !dtIsClosed) &&
        (dtIsOpened24Hours === 'N' || !dtIsOpened24Hours)
      ) {
        dispatch(
          snackbarShowMessage(t('operationHours.driveThruTimeMandatory')),
        );
        return;
      }

      if (request.dtIsOpened24Hours === 'Y') {
        request.dtTimeOpen = '00:00';
        request.dtTimeClose = '23:59';
      }
    } else {
      request.dtIsOpened24Hours = 'N';
      request.dtIsClosed = 'Y';
      request.dtTimeOpen = null;
      request.dtTimeClose = null;
    }

    if (digitalDriveThruService && digitalDriveThruService.value) {
      if (
        id &&
        !digitalDtTimeOpen &&
        (digitalDtIsClosed === 'N' || !digitalDtIsClosed) &&
        (digitalDtIsOpened24Hours === 'N' || !digitalDtIsOpened24Hours)
      ) {
        dispatch(
          snackbarShowMessage(
            t('operationHours.mobileOrderDriveThruTimeMandatory'),
          ),
        );
        return;
      }

      if (request.digitalDtIsOpened24Hours === 'Y') {
        request.digitalDtTimeOpen = '00:00';
        request.digitalDtTimeClose = '23:59';
      }
    } else {
      delete request.digitalDtIsOpened24Hours;
      delete request.digitalDtIsClosed;
      delete request.digitalDtTimeOpen;
      delete request.digitalDtTimeClose;
    }
    if (
      isCalculated === 'N' &&
      request.dhIsOpened24Hours === 'Y' &&
      request.dhIsClosed !== 'Y' &&
      brand === 'BK' &&
      ((Region === 'NA' && Country === 'Canada' && CountryId === 36) || !id)
    ) {
      request.deliveryTimeOpen = '00:00';
      request.deliveryTimeClose = '23:59';
    }
    if (
      !(
        brand === 'BK' &&
        Region === 'NA' &&
        Country === 'Canada' &&
        CountryId === 36
      ) &&
      !(brand === 'BK' && !id)
    ) {
      delete request.dhIsOpened24Hours;
    }
    if (
      request.deliveryTimeOpen === '00:00' &&
      request.deliveryTimeClose === '23:59'
    ) {
      request.dhIsOpened24Hours = 'Y';
    }
    if (isCalculated === 'Y') {
      delete request.deliveryTimeOpen;
      delete request.deliveryTimeClose;
    } else {
      if (
        !(
          Array.isArray(deliveryTimeOpenMode) &&
          deliveryTimeOpenMode.includes('read')
        )
      ) {
        delete request.deliveryTimeOpen;
      }
      if (
        !(
          Array.isArray(deliveryTimeCloseMode) &&
          deliveryTimeCloseMode.includes('read')
        )
      ) {
        delete request.deliveryTimeClose;
      }
    }

    if (!canSeeDeliveryRow(permissions, userInfo.role?.code)) {
      delete request.deliveryTimeOpen;
      delete request.deliveryTimeClose;
      delete request.dhIsOpened24Hours;
      delete request.dhIsClosed;
    }

    if (cateringService && cateringService.value) {
      if (
        id &&
        !(cateringTimeOpen && cateringTimeClose) &&
        ((!checkedAllClose && brand === 'TH') || brand !== 'TH') &&
        checkedClose[4] === 'N' &&
        (brand !== 'PLK' ||
          !isPLKAutoCalculatedLateNight(brand, Region, countries))
      ) {
        dispatch(snackbarShowMessage(t('operationHours.cateringMandatory')));
        return;
      }
    } else {
      delete request.cateringTimeOpen;
      delete request.cateringTimeClose;
    }

    if (curbsideService && curbsideService.value) {
      if (
        id &&
        !(curbsideTimeOpen && curbsideTimeClose) &&
        ((!checkedAllClose && brand === 'TH') || brand !== 'TH') &&
        checkedClose[5] === 'N'
      ) {
        dispatch(snackbarShowMessage(t('operationHours.curbsideMandatory')));
        return;
      }
    } else {
      delete request.curbsideTimeOpen;
      delete request.curbsideTimeClose;
    }

    if (
      !(
        Array.isArray(breakfastTimeOpenMode) &&
        breakfastTimeOpenMode.includes('write')
      )
    ) {
      delete request.breakfastTimeOpen;
    }
    if (
      !(
        Array.isArray(breakfastTimeCloseMode) &&
        breakfastTimeCloseMode.includes('write')
      )
    ) {
      delete request.breakfastTimeClose;
    }

    if (
      !(Array.isArray(lunchTimeOpenMode) && lunchTimeOpenMode.includes('write'))
    ) {
      delete request.lunchTimeOpen;
    }
    if (
      !(
        Array.isArray(lunchTimeCloseMode) &&
        lunchTimeCloseMode.includes('write')
      )
    ) {
      delete request.lunchTimeClose;
    }

    if (
      !(Array.isArray(snackTimeOpenMode) && snackTimeOpenMode.includes('write'))
    ) {
      delete request.snackTimeOpen;
    }
    if (
      !(
        Array.isArray(snackTimeCloseMode) &&
        snackTimeCloseMode.includes('write')
      )
    ) {
      delete request.snackTimeClose;
    }

    if (
      !(
        Array.isArray(dinnerTimeOpenMode) &&
        dinnerTimeOpenMode.includes('write')
      )
    ) {
      delete request.dinnerTimeOpen;
    }
    if (
      !(
        Array.isArray(dinnerTimeCloseMode) &&
        dinnerTimeCloseMode.includes('write')
      )
    ) {
      delete request.dinnerTimeClose;
    }

    if (
      !(
        Array.isArray(lateNightTimeOpenMode) &&
        lateNightTimeOpenMode.includes('write')
      ) ||
      isPLKAutoCalculatedLateNight(brand, Region, [])
    ) {
      delete request.lateNightTimeOpen;
    }
    if (
      !(
        Array.isArray(lateNightTimeCloseMode) &&
        lateNightTimeCloseMode.includes('write')
      )
    ) {
      delete request.lateNightTimeClose;
    }

    let body = {
      setting: 'base',
      ...request,
    };
    if (!id) {
      const restaurants = selectedRestaurants.map(
        restaurant => restaurant.metas?.restaurantId,
      );
      body.isCalculated = 'N';
      body = {
        data: body,
        restaurants,
      };
    }

    try {
      const OHResult = await dispatch(
        setBaseOperationHours(id ? id : 'massUpload', body),
      );
      if (OHResult.status < 300) {
        setEnableBtnEdited(false);
        if (id) {
          const recentlyEditedBaseHours = getRecentlyEditedBaseHours({
            current: body,
            original: originalBaseHoursByDay,
            deliveryAutoCalculationConfig: {
              isCalculated,
              dhIsClosed,
              dtIsOpened24Hours,
              drIsOpened24Hours,
              deliveryOpen,
              deliveryClose,
              deliveryHours,
            },
          });
          dispatch(setRecentlyEditedBaseHours(recentlyEditedBaseHours));

          dispatch(
            snackbarShowMessage(
              `Operation hours for restaurant: ${id} edited `,
            ),
          );
          // Switch to operation hours view mode after successful save
          dispatch(toggleOperationHoursViewMode(true));
        } else {
          dispatch(clearAll());
          setSuccessPopupModal({
            isAlert: true,
            isOpen: true,
            message:
              t('operationHours.success.pendingProcess') +
              ' ' +
              OHResult?.data?.result?.referenceId,
            shouldRedirectOnClose: true,
          });
        }
      } else {
        if (id) {
          dispatch(
            snackbarShowMessage(
              `Error while editing restaurant: ${id} operation hours `,
            ),
          );
        } else {
          dispatch(snackbarShowMessage(t('operationHours.massUpload.error')));
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 'INTERNAL_SERVER_ERROR') {
        setOpenErrorModal(true);
        setErrors(error?.response?.data);
      } else {
        if (id) {
          dispatch(
            snackbarShowMessage(
              `Error while editing restaurant: ${id} operation hours `,
            ),
          );
        } else {
          dispatch(snackbarShowMessage(t('operationHours.massUpload.error')));
        }
      }
    }
  };
  const handleEditionCancel = (e: React.MouseEvent) => {
    if (id) {
      handleEditionCancelForSingleRestaurant();
    } else {
      dispatch(clearAll());
    }
    dispatch(setEdited(false));
    setEnableBtnEdited(false);
  };

  const calculateLateNightTime = useCallback(() => {
    if (!dinnerTimeClose) return undefined;
    const lateNightTimeOpenCalculated = dinnerTimeClose || lateNightTimeOpen;
    let lateNightTimeCloseCalculated: any;
    if (
      checkedAllTF ||
      (drIsOpened24Hours === 'Y' && dtIsOpened24Hours === 'Y')
    ) {
      lateNightTimeCloseCalculated = breakfastTimeOpen;
    } else {
      const driveThruClosing = toMinutes(dtTimeClose);
      const dinningRoomClosing = toMinutes(drTimeClose);

      lateNightTimeCloseCalculated =
        driveThruClosing > dinningRoomClosing ? dtTimeClose : drTimeClose;
    }
    return { lateNightTimeOpenCalculated, lateNightTimeCloseCalculated };
  }, [
    dinnerTimeClose,
    checkedAllTF,
    drIsOpened24Hours,
    dtIsOpened24Hours,
    breakfastTimeOpen,
    dtTimeClose,
    drTimeClose,
  ]);

  function handleEditionCancelForSingleRestaurant() {
    if (weekDays.length) {
      dispatch(getBaseOperationHours(id, weekDays));
    }
  }

  useEffect(() => {
    if (
      driveThruService &&
      !driveThruService.value &&
      (drIsOpened24Hours === 'Y' || drIsClosed === 'Y')
    ) {
      if (
        brand === 'BK' &&
        Region === 'NA' &&
        Country === 'Canada' &&
        CountryId === 36
      ) {
        drIsOpened24Hours === 'Y' &&
        isCalculated === 'N' &&
        dhIsOpened24Hours === 'Y'
          ? setCheckedAllTF(true)
          : setCheckedAllTF(false);
      } else {
        drIsOpened24Hours === 'Y'
          ? setCheckedAllTF(true)
          : setCheckedAllTF(false);
      }
      drIsClosed === 'Y' ? setCheckedAllClose(true) : setCheckedAllClose(false);
    } else {
      setCheckedAllTF(checkedCB.every(el => el === 'Y'));
      setCheckedAllClose(checkedClose.every(el => el === 'Y'));
    }
  }, [
    checkedCB,
    finalPayload,
    checkedClose,
    driveThruService,
    drIsOpened24Hours,
    drIsClosed,
    brand,
    Region,
    Country,
    CountryId,
    isCalculated,
    dhIsOpened24Hours,
    cateringIsClosed,
    curbsideIsClosed,
    dhIsClosed,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearAll());
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      let result;
      const fetchData = async () => {
        result = await dispatch(getBaseOperationHours(id, weekDays));
        if (result?.status !== 200) {
          dispatch(
            snackbarShowMessage(
              `Error while fetching information for restaurant # ${id}`,
            ),
          );
        }
      };
      if (weekDays.length) {
        fetchData();
        setEnableBtnEdited(true);
      }
    } else {
      dispatch(
        getBaseOperationHoursPermission({
          ...operationHoursPermission,
          ...otherBasePermission,
        }),
      );
    }
  }, [dispatch, id, weekDays]);

  const handleCheckAllTF = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setEdited(true));
    if (!checkedAllTF) {
      if (
        brand === 'BK' &&
        ((Region === 'NA' && Country === 'Canada' && CountryId === 36) || !id)
      ) {
        setCheckedCB([...checkedCB.fill('Y', 0, checkedCB.length)]);
        [
          drIsOpened24Hours,
          dtIsOpened24Hours,
          digitalDtIsOpened24Hours,
          dhIsOpened24Hours,
        ].forEach((_, index) =>
          dispatch(
            setOperationHour({
              hourName: hourNames[index],
              hourValue: checkedAllTF ? 'N' : 'Y',
            }),
          ),
        );
      } else {
        setCheckedCB([...checkedCB.fill('Y', 0, checkedCB.length)]);
        [
          drIsOpened24Hours,
          dtIsOpened24Hours,
          digitalDtIsOpened24Hours,
        ].forEach((_, index) =>
          dispatch(
            setOperationHour({
              hourName: hourNames[index],
              hourValue: checkedAllTF ? 'N' : 'Y',
            }),
          ),
        );
      }
      setCheckedAllTF(true);
      setCheckedClose([...checkedClose.fill('N', 0, checkedClose.length)]);
      [
        drIsClosed,
        dtIsClosed,
        digitalDtIsClosed,
        dhIsClosed,
        cateringIsClosed,
        curbsideIsClosed,
      ].forEach((_, index) =>
        dispatch(
          setOperationHour({
            hourName: closeNames[index],
            hourValue: 'N',
          }),
        ),
      );
      setCheckedAllClose(false);
    } else if (checkedAllTF) {
      setCheckedCB([...checkedCB.fill('N', 0, checkedCB.length)]);
      [
        drIsOpened24Hours,
        dtIsOpened24Hours,
        digitalDtIsOpened24Hours,
        dhIsOpened24Hours,
      ].forEach((_, index) => {
        return dispatch(
          setOperationHour({
            hourName: hourNames[index],
            hourValue: checkedAllTF ? 'N' : 'Y',
          }),
        );
      });
      setCheckedAllTF(false);
    }
  };

  const handleCheckAllClose = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setEdited(true));
    if (!checkedAllClose) {
      setCheckedClose([...checkedClose.fill('Y', 0, checkedClose.length)]);
      [
        drIsClosed,
        dtIsClosed,
        digitalDtIsClosed,
        dhIsClosed,
        cateringIsClosed,
        curbsideIsClosed,
      ].forEach((_, index) =>
        dispatch(
          setOperationHour({
            hourName: closeNames[index],
            hourValue: checkedAllClose ? 'N' : 'Y',
          }),
        ),
      );
      setCheckedAllClose(true);
      setCheckedCB([...checkedCB.fill('N', 0, checkedCB.length)]);
      [
        drIsOpened24Hours,
        dtIsOpened24Hours,
        digitalDtIsOpened24Hours,
        dhIsOpened24Hours,
      ].forEach((_, index) =>
        dispatch(
          setOperationHour({
            hourName: hourNames[index],
            hourValue: 'N',
          }),
        ),
      );
      setCheckedAllTF(false);
    } else {
      setCheckedClose([...checkedClose.fill('N', 0, checkedClose.length)]);
      [
        drIsClosed,
        dtTimeClose,
        digitalDtTimeClose,
        dhIsClosed,
        cateringIsClosed,
        curbsideIsClosed,
      ].forEach((_, i) =>
        dispatch(
          setOperationHour({
            hourName: closeNames[i],
            hourValue: checkedAllClose ? 'N' : 'Y',
          }),
        ),
      );
      setCheckedAllClose(false);
    }
  };
  const [deliveryOpen, setDeliveryOpen] = useState<string>('');
  const [deliveryClose, setDeliveryClose] = useState<string>('');
  useEffect(() => {
    const dtTimeOpenIndex = dropdownTimes.findIndex(
      elem =>
        elem === dtTimeOpen && dtTimeOpen !== '' && driveThruService.value,
    );
    const drTimeOpenIndex = dropdownTimes.findIndex(
      elem => elem === drTimeOpen && drTimeOpen !== '',
    );
    const indexesOpenArray: number[] = [
      dtTimeOpenIndex,
      drTimeOpenIndex,
    ].filter(elem => elem > 0);

    const dtTimeCloseIndex = dropdownTimes.findIndex(
      elem => elem === dtTimeClose && dtTimeClose !== '',
    );

    const drTimeCloseIndex = dropdownTimes.findIndex(
      elem => elem === drTimeClose && drTimeClose !== '',
    );
    const indexesCloseArray: number[] = [
      dtTimeCloseIndex,
      drTimeCloseIndex,
    ].filter(elem => elem > 0);
    if (isCalculated === 'Y') {
      const calculatedDeliveryTimeOpen = getCalculatedDeliveryTimeOpen({
        indexesOpenArray,
        deliveryHours,
        brand,
        CountryId,
        dtIsOpened24Hours,
        drIsOpened24Hours,
      });
      setDeliveryOpen(calculatedDeliveryTimeOpen);
      dispatch(
        setOperationHour({
          hourName: 'deliveryTimeOpen',
          hourValue: calculatedDeliveryTimeOpen,
        }),
      );
    } else {
      setDeliveryOpen(deliveryTimeOpen ? deliveryTimeOpen : '');
    }

    if (isCalculated === 'Y') {
      const calculatedDeliveryTimeClose = getCalculatedDeliveryTimeClose({
        dtTimeCloseIndex,
        dtTimeOpenIndex,
        drTimeCloseIndex,
        drTimeOpenIndex,
        indexesCloseArray,
        deliveryHours,
        brand,
        CountryId,
        dtIsOpened24Hours,
        drIsOpened24Hours,
        driveThruService,
      });
      setDeliveryClose(calculatedDeliveryTimeClose);
      dispatch(
        setOperationHour({
          hourName: 'deliveryTimeClose',
          hourValue: calculatedDeliveryTimeClose,
        }),
      );
    } else {
      setDeliveryClose(deliveryTimeClose ? deliveryTimeClose : '');
    }
  }, [
    drTimeOpen,
    dtTimeOpen,
    drTimeClose,
    dtTimeClose,
    drIsOpened24Hours,
    drIsClosed,
    dtIsOpened24Hours,
    dtIsClosed,
    dhIsOpened24Hours,
    brand,
    dispatch,
    deliveryHours,
    deliveryTimeOpen,
    deliveryTimeClose,
    isCalculated,
    CountryId,
    driveThruService,
    cateringIsClosed,
    curbsideIsClosed,
    dhIsClosed,
  ]);

  useEffect(() => {
    if (isPLKAutoCalculatedLateNight(brand, Region, [])) {
      const calculated = calculateLateNightTime();
      if (!calculated) return;
      dispatch(
        setOperationHour({
          hourName: 'lateNightTimeClose',
          hourValue: calculated.lateNightTimeCloseCalculated,
        }),
      );
      dispatch(
        setOperationHour({
          hourName: 'lateNightTimeOpen',
          hourValue: calculated.lateNightTimeOpenCalculated,
        }),
      );
    }
  }, [
    dinnerTimeClose,
    dtTimeClose,
    breakfastTimeOpen,
    drTimeClose,
    Region,
    brand,
    calculateLateNightTime,
    dispatch,
  ]);
  const selectDaysTabletAppBar = (
    <div>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12} direction="row">
            <Grid item lg={4} xs={4} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.selectDays')}</Typography>
            </Grid>
            <Grid item lg={6} xs={1} style={{ margin: 'auto' }}></Grid>
            <Grid item lg={6} xs={1} style={{ margin: 'auto' }}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );

  const selectDaysDesktopAppBar = (
    <div>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12}>
            <Grid item lg={4} xs={4} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.selectDays')}</Typography>
            </Grid>
            <Grid item lg={6} xs={1} style={{ margin: 'auto' }}></Grid>
            <Grid item lg={1} xs={4} style={{ margin: 'auto' }}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );

  const tabletAppBar = (
    <div>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12} direction="row">
            <Grid item lg={4} xs={4} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.timetable')}</Typography>
            </Grid>
            <Grid item lg={2} xs={2}>
              <div className={classes.tableCheckbox}>
                <div>
                  <Typography style={{ padding: 0 }}>
                    24<small>hrs</small>
                  </Typography>
                  <Checkbox
                    checked={checkedAllTF}
                    onChange={handleCheckAllTF}
                    style={{ color: 'white', padding: 0 }}
                    disabled={!hasEditPermission}
                  />
                </div>
                &nbsp;
                <div>
                  <Typography style={{ padding: 0 }}>
                    {t('operationHours.close')}
                  </Typography>
                  <Checkbox
                    checked={checkedAllClose}
                    onChange={handleCheckAllClose}
                    style={{ color: 'white', padding: 0 }}
                    disabled={!hasEditPermission}
                  />
                </div>
              </div>
            </Grid>
            <Grid item lg={3} xs={3} style={{ margin: 'auto' }}></Grid>
            <Grid item lg={3} xs={3} style={{ margin: 'auto' }}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );
  const desktopAppBar = (
    <div>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12}>
            <Grid item lg={4} md={4} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.timetable')}</Typography>
            </Grid>
            <Grid item lg={2} md={2}>
              <div className={classes.desktopCheckbox}>
                <div className={classes.desktopCheckboxControl}>
                  <Typography style={{ padding: 0 }}>
                    <small>{t('app.common.twentyFourHour')}</small>
                  </Typography>
                  <Checkbox
                    checked={checkedAllTF}
                    onChange={handleCheckAllTF}
                    style={{ color: 'white', padding: 0 }}
                    disabled={!hasEditPermission}
                  />
                </div>
                &nbsp;
                <div className={classes.desktopCheckboxControl}>
                  {' '}
                  <Typography style={{ padding: 0 }}>
                    {' '}
                    <small> {t('operationHours.closed')}</small>
                  </Typography>
                  <Checkbox
                    checked={checkedAllClose}
                    onChange={handleCheckAllClose}
                    style={{ color: 'white', padding: 0 }}
                    disabled={!hasEditPermission}
                  />
                </div>
              </div>
            </Grid>
            <Grid item lg={3} md={3} style={{ margin: 'auto' }}></Grid>
            <Grid item lg={3} md={3} style={{ margin: 'auto' }}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );

  const requiredFieldText = !id ? '*' : '';
  const requiredFieldProps = !id
    ? { display: 'inline', color: 'error', variant: 'h5' }
    : undefined;
  const optionalFieldText = !id ? <sup>++</sup> : '';
  const optionalFieldProps = !id
    ? { display: 'inline', style: { fontSize: '12px' } }
    : undefined;

  const enableDh24hr =
    (isCalculated === 'N' &&
      brand === 'BK' &&
      Region === 'NA' &&
      Country === 'Canada' &&
      CountryId === 36) ||
    (isCalculated === 'N' && brand === 'BK' && !id) ||
    (isCalculated === 'N' && brand === 'TH');

  useEffect(() => {
    if (
      !drTimeOpen &&
      !drTimeClose &&
      !dtTimeOpen &&
      !dtTimeClose &&
      weekDays.length &&
      isCalculated === 'Y'
    ) {
      dispatch(
        setOperationHour({
          hourName: 'dhIsOpened24Hours',
          hourValue: 'N',
        }),
      );
      dispatch(
        setOperationHour({
          hourName: 'dhIsClosed',
          hourValue: 'Y',
        }),
      );
    }
  }, [drTimeOpen, drTimeClose, dtTimeOpen, dtTimeClose, dispatch]);

  function shouldCancelButtonDisabled() {
    if (!hasEditPermission) return true;
    if (id && isRestaurantClosed) return true;
    if (weekDays.length && enableBtnEdited) return false;
    return true;
  }

  function shouldSaveButtonDisabled() {
    if (!hasEditPermission) return true;
    if (id && isRestaurantClosed) return true;
    if (!id && !selectedRestaurants.length) return true;
    if (weekDays.length && enableBtnEdited) return false;
    return true;
  }

  function getTooltip() {
    if (isCalculated === 'Y') {
      return t('operationHours.isCalculatedMessage');
    }
    return !canSeeDeliveryRow(permissions, userInfo.role?.code)
      ? t('operationHours.cantEditDeliveryMessage')
      : '';
  }

  function hasEditAccess() {
    if (!weekDays.length) return false;
    if (!isMDSUpAndRunning) return false;
    if (id) return hasEditHoursOfOperation && !isRestaurantClosed;
    return hasViewMenuItemOperationHours;
  }

  function hasEditDaypartHoursAccess(brand: string) {
    if (!hasEditAccess()) return false;
    // Only restrict editing for TH Canada franchisees (auto-calculated)
    if (brand !== 'TH' || Country !== 'Canada') return true;
    return isCorporateAdmin();
  }

  return (
    <React.Fragment>
      <Grid item xs={12} lg={3}>
        {matches ? selectDaysDesktopAppBar : selectDaysTabletAppBar}
        <Paper className={classes.paper2}>
          <div style={{ padding: '16px' }}>
            <WeekDaysSelection targetReducer="OperationHoursReducer" />
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} lg={6}>
        {matches ? desktopAppBar : tabletAppBar}
        <RBIFollowCursorTooltip title={tooltipMessage}>
          <Paper className={classes.paper2}>
            <ZoneListItem
              primary={t('operationHours.storeArrival')}
              primaryTypographyProps={{ display: 'inline' }}
              secondary={!checkedAllClose ? requiredFieldText : ''}
              secondaryTypographyProps={requiredFieldProps}
              firstTName={t('operationHours.specialEventArrival')}
              checked={checkedCB}
              checkedClose={checkedClose}
              handleChecked={setCheckedCB}
              handleCheckedClose={setCheckedClose}
              index={6}
              times={arrivalTimes}
              startTime={arrivalTime}
              startTimeName={varToString({ arrivalTime })}
              setEdited={setEdited}
              hasEditPermission={hasEditPermission}
            />

            {brand === 'TH' && (Region === 'NA' || !id) && (
              <ZoneListItem
                primary={t('operationHours.storeDeparture')}
                primaryTypographyProps={{ display: 'inline' }}
                secondary={!checkedAllClose ? requiredFieldText : ''}
                secondaryTypographyProps={requiredFieldProps}
                firstTName={t('operationHours.specialEventDeparture')}
                checked={checkedCB}
                checkedClose={checkedClose}
                handleChecked={setCheckedCB}
                handleCheckedClose={setCheckedClose}
                index={6}
                times={arrivalTimes}
                startTime={departureTime}
                startTimeName={varToString({ departureTime })}
                setEdited={setEdited}
                hasEditPermission={hasEditPermission}
              />
            )}

            <ZoneListItem
              primary={t('operationHours.diningRoom')}
              primaryTypographyProps={{ display: 'inline' }}
              secondary={requiredFieldText}
              secondaryTypographyProps={requiredFieldProps}
              firstTName={t('operationHours.start')}
              secondTName={t('operationHours.finish')}
              checked={checkedCB}
              handleChecked={setCheckedCB}
              index={0}
              startTime={drTimeOpen}
              startTimeMode={drTimeOpenMode}
              endTime={drTimeClose}
              endTimeMode={drTimeCloseMode}
              startTimeName={varToString({ drTimeOpen })}
              endTimeName={varToString({ drTimeClose })}
              twentyFourHourName={varToString({ drIsOpened24Hours })}
              checkedClose={checkedClose}
              handleCheckedClose={setCheckedClose}
              closeHourName={varToString({ drIsClosed })}
              hasCheckboxes={true}
              setEdited={setEdited}
              times={dropdownTimes}
              hasEditPermission={hasEditPermission}
            />
            {driveThruService && driveThruService.value ? (
              <ZoneListItem
                primary={t('operationHours.driveThru')}
                primaryTypographyProps={{ display: 'inline' }}
                secondary={requiredFieldText}
                secondaryTypographyProps={requiredFieldProps}
                firstTName={t('operationHours.start')}
                secondTName={t('operationHours.finish')}
                checked={checkedCB}
                checkedClose={checkedClose}
                handleChecked={setCheckedCB}
                handleCheckedClose={setCheckedClose}
                index={1}
                startTime={dtTimeOpen}
                startTimeMode={dtTimeOpenMode}
                endTime={dtTimeClose}
                endTimeMode={dtTimeCloseMode}
                startTimeName={varToString({ dtTimeOpen })}
                endTimeName={varToString({ dtTimeClose })}
                twentyFourHourName={varToString({ dtIsOpened24Hours })}
                closeHourName={varToString({ dtIsClosed })}
                setEdited={setEdited}
                hasCheckboxes={true}
                times={dropdownTimes}
                hasEditPermission={hasEditPermission}
              />
            ) : null}
            {brand === 'BK' &&
            (Region === 'NA' || !id) &&
            digitalDriveThruService &&
            digitalDriveThruService.value ? (
              <ZoneListItem
                primary={t('operationHours.mobileOrderDriveThru')}
                primaryTypographyProps={{ display: 'inline' }}
                secondary={optionalFieldText}
                secondaryTypographyProps={optionalFieldProps}
                firstTName={t('operationHours.start')}
                secondTName={t('operationHours.finish')}
                checked={checkedCB}
                checkedClose={checkedClose}
                handleChecked={setCheckedCB}
                handleCheckedClose={setCheckedClose}
                index={2}
                startTime={digitalDtTimeOpen}
                startTimeMode={digitalDtTimeOpenMode}
                endTime={digitalDtTimeClose}
                endTimeMode={digitalDtTimeCloseMode}
                startTimeName={varToString({ digitalDtTimeOpen })}
                endTimeName={varToString({ digitalDtTimeClose })}
                twentyFourHourName={varToString({ digitalDtIsOpened24Hours })}
                closeHourName={varToString({ digitalDtIsClosed })}
                setEdited={setEdited}
                hasCheckboxes={true}
                times={dropdownTimes}
                hasEditPermission={hasEditPermission}
              />
            ) : null}
            <ZoneListItem
              primary={t('operationHours.deliveryTime')}
              firstTName={t('operationHours.start')}
              secondTName={t('operationHours.finish')}
              isDisabled={isCalculated === 'Y'}
              enableDhTime24hr={enableDh24hr}
              checked={checkedCB}
              checkedClose={checkedClose}
              handleChecked={setCheckedCB}
              handleCheckedClose={setCheckedClose}
              index={3}
              tooltipTitle={getTooltip()}
              startTime={deliveryOpen}
              startTimeMode={deliveryTimeOpenMode}
              endTime={deliveryClose}
              endTimeMode={deliveryTimeCloseMode}
              twentyFourHourName={
                enableDh24hr ? varToString({ dhIsOpened24Hours }) : undefined
              }
              startTimeName={varToString({ deliveryTimeOpen })}
              endTimeName={varToString({ deliveryTimeClose })}
              setEdited={setEdited}
              closeHourName={varToString({ dhIsClosed })}
              hasCheckboxes={true}
              times={dropdownTimes}
              hasEditPermission={
                hasEditPermission &&
                canSeeDeliveryRow(permissions, userInfo.role?.code)
              }
            />
            {cateringService &&
            cateringService.value &&
            (brand !== 'PLK' ||
              !isPLKAutoCalculatedLateNight(brand, Region, countries)) ? (
              <ZoneListItem
                primary={t('operationHours.cateringTime')}
                primaryTypographyProps={{ display: 'inline' }}
                secondary={optionalFieldText}
                secondaryTypographyProps={optionalFieldProps}
                firstTName={t('operationHours.start')}
                secondTName={t('operationHours.finish')}
                checked={checkedCB}
                checkedClose={checkedClose}
                handleChecked={setCheckedCB}
                handleCheckedClose={setCheckedClose}
                index={4}
                startTime={cateringTimeOpen}
                startTimeMode={cateringTimeOpenMode}
                endTime={cateringTimeClose}
                endTimeMode={cateringTimeCloseMode}
                startTimeName={varToString({ cateringTimeOpen })}
                endTimeName={varToString({ cateringTimeClose })}
                closeHourName={varToString({ cateringIsClosed })}
                setEdited={setEdited}
                times={dropdownTimes}
                hasCheckboxes={true}
                hasCloseCheckbox={true}
                hasEditPermission={hasEditPermission}
              />
            ) : null}
            {curbsideService && curbsideService.value ? (
              <ZoneListItem
                primary={t('operationHours.curbsideTime')}
                primaryTypographyProps={{ display: 'inline' }}
                secondary={optionalFieldText}
                secondaryTypographyProps={optionalFieldProps}
                firstTName={t('operationHours.start')}
                secondTName={t('operationHours.finish')}
                checked={checkedCB}
                checkedClose={checkedClose}
                handleChecked={setCheckedCB}
                handleCheckedClose={setCheckedClose}
                index={5}
                startTime={curbsideTimeOpen}
                startTimeMode={curbsideTimeOpenMode}
                endTime={curbsideTimeClose}
                checkedAll
                endTimeMode={curbsideTimeCloseMode}
                startTimeName={varToString({ curbsideTimeOpen })}
                endTimeName={varToString({ curbsideTimeClose })}
                closeHourName={varToString({ curbsideIsClosed })}
                setEdited={setEdited}
                times={dropdownTimes}
                hasCloseCheckbox={true}
                hasEditPermission={hasEditPermission}
              />
            ) : null}
            {(brand !== 'PLK' ||
              !isPLKAutoCalculatedLateNight(brand, Region, countries)) && (
              <>
                <ZoneListItem
                  primary={t('operationHours.breakfastTime')}
                  secondary={t('operationHours.daypart')}
                  isDayPart={true}
                  firstTName={t('operationHours.start')}
                  secondTName={t('operationHours.finish')}
                  checked={checkedCB}
                  checkedClose={checkedClose}
                  handleChecked={setCheckedCB}
                  handleCheckedClose={setCheckedClose}
                  index={6}
                  startTime={breakfastTimeOpen}
                  startTimeMode={breakfastTimeOpenMode}
                  endTime={breakfastTimeClose}
                  endTimeMode={breakfastTimeCloseMode}
                  startTimeName={varToString({ breakfastTimeOpen })}
                  endTimeName={varToString({ breakfastTimeClose })}
                  setEdited={setEdited}
                  times={dropdownTimes}
                  hasEditPermission={hasEditDaypartHoursPermission}
                />
                <ZoneListItem
                  primary={t('operationHours.lunchTime')}
                  secondary={t('operationHours.daypart')}
                  isDayPart={true}
                  firstTName={t('operationHours.start')}
                  secondTName={t('operationHours.finish')}
                  checked={checkedCB}
                  checkedClose={checkedClose}
                  handleChecked={setCheckedCB}
                  handleCheckedClose={setCheckedClose}
                  index={7}
                  startTime={lunchTimeOpen}
                  startTimeMode={lunchTimeOpenMode}
                  endTime={lunchTimeClose}
                  endTimeMode={lunchTimeCloseMode}
                  startTimeName={varToString({ lunchTimeOpen })}
                  endTimeName={varToString({ lunchTimeClose })}
                  setEdited={setEdited}
                  times={dropdownTimes}
                  hasEditPermission={hasEditDaypartHoursPermission}
                />
                <ZoneListItem
                  primary={t('operationHours.snackTime')}
                  secondary={t('operationHours.daypart')}
                  isDayPart={true}
                  firstTName={t('operationHours.start')}
                  secondTName={t('operationHours.finish')}
                  checked={checkedCB}
                  checkedClose={checkedClose}
                  handleChecked={setCheckedCB}
                  handleCheckedClose={setCheckedClose}
                  index={8}
                  startTime={snackTimeOpen}
                  startTimeMode={snackTimeOpenMode}
                  endTime={snackTimeClose}
                  endTimeMode={snackTimeCloseMode}
                  startTimeName={varToString({ snackTimeOpen })}
                  endTimeName={varToString({ snackTimeClose })}
                  setEdited={setEdited}
                  times={dropdownTimes}
                  hasEditPermission={hasEditDaypartHoursPermission}
                />
                <ZoneListItem
                  primary={t('operationHours.dinnerTime')}
                  secondary={t('operationHours.daypart')}
                  isDayPart={true}
                  firstTName={t('operationHours.start')}
                  secondTName={t('operationHours.finish')}
                  checked={checkedCB}
                  checkedClose={checkedClose}
                  handleChecked={setCheckedCB}
                  handleCheckedClose={setCheckedClose}
                  index={9}
                  startTime={dinnerTimeOpen}
                  startTimeMode={dinnerTimeOpenMode}
                  endTime={dinnerTimeClose}
                  endTimeMode={dinnerTimeCloseMode}
                  startTimeName={varToString({ dinnerTimeOpen })}
                  endTimeName={varToString({ dinnerTimeClose })}
                  setEdited={setEdited}
                  times={dropdownTimes}
                  hasEditPermission={hasEditDaypartHoursPermission}
                />
                <ZoneListItem
                  primary={t('operationHours.lateNight')}
                  secondary={t('operationHours.daypart')}
                  isDayPart={true}
                  firstTName={t('operationHours.start')}
                  secondTName={t('operationHours.finish')}
                  checked={checkedCB}
                  checkedClose={checkedClose}
                  handleChecked={setCheckedCB}
                  handleCheckedClose={setCheckedClose}
                  index={10}
                  startTime={lateNightTimeOpen}
                  startTimeMode={lateNightTimeOpenMode}
                  endTime={lateNightTimeClose}
                  endTimeMode={lateNightTimeCloseMode}
                  startTimeName={varToString({ lateNightTimeOpen })}
                  endTimeName={varToString({ lateNightTimeClose })}
                  setEdited={setEdited}
                  times={dropdownTimes}
                  hasEditPermission={hasEditDaypartHoursPermission}
                />
              </>
            )}
          </Paper>
        </RBIFollowCursorTooltip>
        <div>
          <Grid
            container
            direction="row"
            justify="flex-end"
            style={{ marginTop: 8 }}
          >
            <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
              <Button
                disabled={shouldCancelButtonDisabled()}
                variant="contained"
                color="primary"
                onClick={handleEditionCancel}
                style={{ marginRight: 10 }}
              >
                {t('app.common.cancel')}
              </Button>
            </Grid>
            <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
              <Button
                disabled={shouldSaveButtonDisabled()}
                variant="contained"
                color="primary"
                onClick={handleEditionSubmit}
              >
                {t('app.common.save')}
              </Button>
            </Grid>
          </Grid>
        </div>
      </Grid>
      {successPopupModal.isOpen && (
        <RBISuccessPopup
          isOpen={successPopupModal.isOpen}
          isAlert={successPopupModal.isAlert}
          closeModal={handleSuccessCloseModal}
          message={successPopupModal.message}
        />
      )}
      {openErrorModal && (
        <ShowErrorMessageModal
          isOpen={openErrorModal}
          onClose={() => setOpenErrorModal(false)}
          errors={uploadErrors}
        />
      )}
    </React.Fragment>
  );
}

export default BaseAdjustment;

function getCalculatedDeliveryTimeClose({
  dtTimeCloseIndex,
  dtTimeOpenIndex,
  drTimeCloseIndex,
  drTimeOpenIndex,
  indexesCloseArray,
  deliveryHours,
  brand,
  CountryId,
  dtIsOpened24Hours,
  drIsOpened24Hours,
  driveThruService,
}) {
  if (!indexesCloseArray.length) return '';
  // Determining which index to be considered for delivery time calculation.
  let deliveryCloseTimeIndex = 0;
  let dtCloseTimeOffSet = 0;
  let drCloseTimeOffSet = 0;
  const midNightTime = moment('00:00', 'HH:mm');

  if (
    driveThruService &&
    driveThruService.value &&
    dtTimeCloseIndex &&
    dtTimeOpenIndex &&
    dtTimeCloseIndex !== dtTimeOpenIndex
  ) {
    const dtTimeClose = moment(dropdownTimes[dtTimeCloseIndex], 'HH:mm');
    const dtTimeOpen = moment(dropdownTimes[dtTimeOpenIndex], 'HH:mm');
    dtCloseTimeOffSet = dtTimeClose.diff(midNightTime, 'minutes');
    if (dtTimeClose.isBefore(dtTimeOpen)) {
      dtCloseTimeOffSet += 24 * 60;
    }
  }

  if (
    drTimeCloseIndex &&
    drTimeOpenIndex &&
    drTimeCloseIndex !== drTimeOpenIndex
  ) {
    const drTimeClose = moment(dropdownTimes[drTimeCloseIndex], 'HH:mm');
    const drTimeOpen = moment(dropdownTimes[drTimeOpenIndex], 'HH:mm');
    drCloseTimeOffSet = drTimeClose.diff(midNightTime, 'minutes');
    if (drTimeClose.isBefore(drTimeOpen)) {
      drCloseTimeOffSet += 24 * 60;
    }
  }

  deliveryCloseTimeIndex =
    dtCloseTimeOffSet >= drCloseTimeOffSet
      ? dtTimeCloseIndex
      : drTimeCloseIndex;
  if (!deliveryHours) return dropdownTimes[deliveryCloseTimeIndex];
  if (!deliveryHours.brand === brand || !deliveryHours.country === CountryId)
    return dropdownTimes[deliveryCloseTimeIndex];
  if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y')
    return calculateDeliveryTimeBase({
      currentTime: dropdownTimes[deliveryCloseTimeIndex],
      time: deliveryHours.closing,
      isOpenTime: false,
    });
  return deliveryHours.default24HourTimeClose !== null
    ? deliveryHours.default24HourTimeClose
    : '00:00';
}

function getCalculatedDeliveryTimeOpen({
  indexesOpenArray,
  deliveryHours,
  brand,
  CountryId,
  dtIsOpened24Hours,
  drIsOpened24Hours,
}) {
  if (!indexesOpenArray.length) return '';
  const minIndex = Math.min(...indexesOpenArray);

  if (!deliveryHours) return dropdownTimes[minIndex];
  if (deliveryHours.brand !== brand || deliveryHours.country !== CountryId)
    return dropdownTimes[minIndex];

  if (dtIsOpened24Hours !== 'Y' && drIsOpened24Hours !== 'Y')
    return calculateDeliveryTimeBase({
      currentTime: dropdownTimes[minIndex],
      time: deliveryHours.opening,
      isOpenTime: true,
      minimumOpeningTime: deliveryHours.minimumOpenTime,
    });

  return deliveryHours.default24HourTimeOpen !== null
    ? deliveryHours.default24HourTimeOpen
    : '00:00';
}

// Helper function to track recently edited base hours for selected weekdays
function getRecentlyEditedBaseHours({
  current,
  original,
  deliveryAutoCalculationConfig,
}: {
  current: Record<string, string | undefined>;
  original: Record<string, Record<string, string | undefined>>;
  deliveryAutoCalculationConfig?: {
    isCalculated: string;
    dhIsClosed: string;
    dtIsOpened24Hours: string;
    drIsOpened24Hours: string;
    deliveryOpen: string;
    deliveryClose: string;
    deliveryHours?: {
      default24HourTimeOpen?: string;
      default24HourTimeClose?: string;
    };
  };
}): RecentlyEditedBaseHours {
  const recentlyEditedFields: RecentlyEditedBaseHours = {};

  // When isCalculated is 'Y', include the actual delivery times shown to user
  const dataForTracking = { ...current };
  if (
    deliveryAutoCalculationConfig &&
    deliveryAutoCalculationConfig.isCalculated === 'Y' &&
    deliveryAutoCalculationConfig.dhIsClosed !== 'Y'
  ) {
    // Use the same logic as TimePickerDropDown to get the displayed values
    let deliveryOpenValue = deliveryAutoCalculationConfig.deliveryOpen;
    let deliveryCloseValue = deliveryAutoCalculationConfig.deliveryClose;

    // When both dining room and drive-thru are 24 hours, use values from deliveryHours
    if (
      deliveryAutoCalculationConfig.dtIsOpened24Hours === 'Y' ||
      deliveryAutoCalculationConfig.drIsOpened24Hours === 'Y'
    ) {
      deliveryOpenValue =
        deliveryAutoCalculationConfig.deliveryHours?.default24HourTimeOpen ||
        deliveryOpenValue;
      deliveryCloseValue =
        deliveryAutoCalculationConfig.deliveryHours?.default24HourTimeClose ||
        deliveryCloseValue;
    }

    dataForTracking.deliveryTimeOpen = deliveryOpenValue;
    dataForTracking.deliveryTimeClose = deliveryCloseValue;
  }

  // Only check weekdays that are available in current data and have original base hours
  if (dataForTracking.weekDays && Array.isArray(dataForTracking.weekDays)) {
    dataForTracking.weekDays.forEach((dayKey: string) => {
      const originalDayBaseHours = original[dayKey];
      if (!originalDayBaseHours) return; // Skip if no original base hours for this day

      const dayUpdates: RecentlyEditedDayData = {};

      Object.keys(dataForTracking).forEach(field => {
        // Skip non-service fields like 'setting', 'weekDays', etc.
        if (field === 'setting' || field === 'weekDays') {
          return;
        }

        const originalValue = originalDayBaseHours[field];
        const currentValue = dataForTracking[field];

        if (originalValue !== currentValue) {
          dayUpdates[field] = {
            current: currentValue,
          };
        }
      });

      // Only add to recentlyEditedFields if there are actual updates for this day
      if (Object.keys(dayUpdates).length > 0) {
        recentlyEditedFields[dayKey] = dayUpdates;
      }
    });
  }
  return recentlyEditedFields;
}
